import { SHARED } from '@/constants'

/**
 * Turning a page's search criteria into query parameters.
 *
 * ── Why this is shared ──────────────────────────────────────────────────
 * one_sales repeats a private `buildParams` in every service, and they have
 * drifted: some send `is_active` only when it is non-empty, some send it
 * always, and the ones that send `-1` (the "All" sentinel) relied on each
 * filter class on the server treating it as "no constraint". One of them did
 * not, and that listing came back empty whenever All was selected.
 *
 * The rules, in one place:
 *   - the "All" sentinel (-1 / '') is DROPPED, never sent
 *   - an empty array is dropped, so an untouched multi-select adds nothing
 *   - everything else goes out as-is and qs serialises arrays as `k[0]=`,
 *     which is what Laravel parses back into an array
 *
 * @param {object} filter  the page's searchCriteria (pageNumber, pageSize,
 *                         searchArray, plus whatever that listing filters on)
 * @param {object} extra   listing-specific keys, same value rules
 */
export function buildListingParams(filter, extra = {}) {
  const params = {
    page: filter?.pageNumber || SHARED.DEFAULT_PAGE_NUMBER,
    size: filter?.pageSize || SHARED.DEFAULT_PAGE_SIZE,
  }

  if (filter?.sortBy) params.sort_by = filter.sortBy
  if (filter?.orderBy) params.order_by = filter.orderBy

  // The free-text box. Sent as an array because the server ORs the words, so
  // "ace admin" finds a row matching either.
  const words = (filter?.searchArray || []).filter((w) => !isAll(w))
  if (words.length) params.search_words = words

  Object.keys(extra).forEach((key) => {
    const value = clean(extra[key])
    if (value !== undefined) params[key] = value
  })

  return params
}

/**
 * The SPA's "All" markers, which must never reach the server as a value.
 *
 * -1 is SHARED.DEFAULT_ALL_VALUE and '' is DEFAULT_ALL_VALUE_STR; both mean
 * "do not filter on this column". The server also defends against them (see
 * App\Helpers\Filters\FilterValues) because a bookmarked URL can still carry
 * one — but not sending them keeps the request legible in a network tab.
 */
function isAll(value) {
  return (
    value === null ||
    value === undefined ||
    value === '' ||
    value === SHARED.DEFAULT_ALL_VALUE ||
    String(value) === String(SHARED.DEFAULT_ALL_VALUE)
  )
}

/** Returns undefined for anything that should be left out of the query. */
function clean(value) {
  if (Array.isArray(value)) {
    const kept = value.filter((v) => !isAll(v))
    return kept.length ? kept : undefined
  }

  return isAll(value) ? undefined : value
}
